import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './EditOpinionFieldsPage.css';

const API_BASE_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  return url ? url + '/api' : '/api';
})();

// =============================================================================
// FIELD GROUPING CONFIG (Frontend handles grouping, not backend)
// =============================================================================
const FIELD_GROUPS = {
  document_info: {
    label: "Document Info",
    icon: "📌",
    fields: ["document_type", "document_number", "document_date", "sro", "original_status"]
  },
  parties: {
    label: "Parties",
    icon: "👤",
    fields: ["vendor", "vendee", "executed_by", "in_favour_of"]
  },
  property_details: {
    label: "Property Details",
    icon: "🧭",
    fields: ["survey_number", "village", "mandal", "district"]
  },
  extent: {
    label: "Extent",
    icon: "📏",
    fields: ["extent_sqyds", "extent_sqmtrs"]
  },
  boundaries: {
    label: "Boundaries",
    icon: "🗺️",
    fields: ["boundaries_north", "boundaries_south", "boundaries_east", "boundaries_west"]
  },
  consideration: {
    label: "Consideration",
    icon: "💰",
    fields: ["consideration", "loan_amount"]
  }
};

// Critical fields that must be fixed (mandatory across ALL property docs
// — Feb 2026 user directive). Matches ExtractionReviewPage.js + backend.
const CRITICAL_FIELDS = [
  "document_date", "document_number", "vendor", "vendee",
  "executed_by", "in_favour_of", "survey_number", "loan_amount",
  "ec_from_date", "ec_to_date",
  "extent_sqyds", "extent_sqmtrs",
];

// Extent either-or rule — filling either Sq.Yds or Sq.Mtrs satisfies
// the mandate.
const isExtentSatisfied = (docFields) => {
  const sy = (docFields?.extent_sqyds?.value || '').toString().trim();
  const sm = (docFields?.extent_sqmtrs?.value || '').toString().trim();
  return sy.length > 0 || sm.length > 0;
};

// Field display names
const FIELD_LABELS = {
  document_type: "Document Type",
  document_number: "Document Number",
  document_date: "Document Date",
  sro: "Sub-Registrar Office",
  original_status: "Original/Xerox",
  vendor: "Vendor (Seller)",
  vendee: "Vendee (Buyer)",
  executed_by: "Executed By",
  in_favour_of: "In Favour Of",
  survey_number: "Survey Number",
  village: "Village",
  mandal: "Mandal",
  district: "District",
  extent_sqyds: "Extent (Sq. Yards)",
  extent_sqmtrs: "Extent (Sq. Meters)",
  boundaries_north: "North Boundary",
  boundaries_south: "South Boundary",
  boundaries_east: "East Boundary",
  boundaries_west: "West Boundary",
  consideration: "Consideration Amount",
  loan_amount: "Loan Amount"
};

// =============================================================================
// RISK INDICATOR COMPONENT
// =============================================================================
// HCG status is intentionally binary: RED only for missing mandatory
// (CRITICAL_FIELDS) values; GREEN for everything else. No yellow / amber
// / "Review" tier — optional fields never flag warnings.
const RiskBadge = ({ risk }) => {
  const badges = {
    green: { icon: "✅", className: "risk-green", label: "Valid" },
    red:   { icon: "🔴", className: "risk-red",   label: "Must Fix" }
  };
  const badge = risk === 'red' ? badges.red : badges.green;
  return <span className={`risk-badge ${badge.className}`} title={badge.label}>{badge.icon}</span>;
};

// =============================================================================
// EDITABLE FIELD COMPONENT
// =============================================================================
const EditableField = ({ fieldName, fieldData, onChange, isCritical }) => {
  const { value, risk } = fieldData;
  const label = FIELD_LABELS[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  
  return (
    <div className={`editable-field ${risk === 'red' ? 'field-error' : 'field-valid'}`}>
      <label>
        {label}
        {isCritical && <span className="critical-marker">*</span>}
        <RiskBadge risk={risk} />
      </label>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(fieldName, e.target.value)}
        className={`field-input risk-${risk}`}
        placeholder={`Enter ${label.toLowerCase()}`}
      />
    </div>
  );
};

// =============================================================================
// FIELD GROUP COMPONENT
// =============================================================================
const FieldGroup = ({ groupKey, groupConfig, fields, onChange }) => {
  const { label, icon, fields: fieldKeys } = groupConfig;
  
  // Filter to only show fields that exist in the document
  const availableFields = fieldKeys.filter(key => fields[key]);
  
  if (availableFields.length === 0) return null;
  
  return (
    <div className="field-group">
      <h3 className="field-group-title">{icon} {label}</h3>
      <div className="field-group-content">
        {availableFields.map(fieldKey => (
          <EditableField
            key={fieldKey}
            fieldName={fieldKey}
            fieldData={fields[fieldKey]}
            onChange={onChange}
            isCritical={CRITICAL_FIELDS.includes(fieldKey)}
          />
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// DOCUMENT TAB COMPONENT
// =============================================================================
const DocumentTab = ({ doc, index, isActive, onClick }) => {
  const statusIcons = {
    green: "✅",
    red: "🔴"
  };

  return (
    <button
      className={`doc-tab ${isActive ? 'active' : ''} status-${doc.doc_status}`}
      onClick={() => onClick(index)}
    >
      <span className="doc-tab-status">{statusIcons[doc.doc_status] || statusIcons.green}</span>
      <span className="doc-tab-type">{doc.document_type}</span>
      <span className="doc-tab-index">#{index + 1}</span>
    </button>
  );
};

// =============================================================================
// SUMMARY BAR COMPONENT
// =============================================================================
const SummaryBar = ({ summary, onJumpToErrors }) => {
  const { total_docs, docs_with_issues, critical_errors, can_generate } = summary;
  
  return (
    <div className={`summary-bar ${can_generate ? 'can-generate' : 'has-errors'}`}>
      <div className="summary-stats">
        <span className="stat">
          <span className="stat-value">{total_docs}</span>
          <span className="stat-label">Documents</span>
        </span>
        <span className="stat-divider">|</span>
        <span className={`stat ${docs_with_issues > 0 ? 'warning' : ''}`}>
          <span className="stat-value">{docs_with_issues}</span>
          <span className="stat-label">With Issues</span>
        </span>
        <span className="stat-divider">|</span>
        <span className={`stat ${critical_errors > 0 ? 'error' : ''}`}>
          <span className="stat-value">{critical_errors}</span>
          <span className="stat-label">Critical Errors</span>
        </span>
      </div>
      {critical_errors > 0 && (
        <button className="jump-errors-btn" onClick={onJumpToErrors}>
          Jump to Errors
        </button>
      )}
    </div>
  );
};

// =============================================================================
// PROGRESS BAR COMPONENT
// =============================================================================
const ProgressBar = ({ documents, activeIndex, onSelect }) => {
  return (
    <div className="progress-bar">
      {documents.map((doc, idx) => (
        <button
          key={idx}
          className={`progress-dot status-${doc.doc_status} ${idx === activeIndex ? 'active' : ''}`}
          onClick={() => onSelect(idx)}
          title={`${doc.document_type} - ${doc.doc_status.toUpperCase()}`}
        >
          {idx + 1}
        </button>
      ))}
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const EditOpinionFieldsPage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Document-wise state
  const [summary, setSummary] = useState({ total_docs: 0, docs_with_issues: 0, critical_errors: 0, can_generate: true });
  const [documents, setDocuments] = useState([]);
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [metadata, setMetadata] = useState({});
  const [extractionId, setExtractionId] = useState(null);
  
  // Legacy fields for backward compatibility
  const [legacyFields, setLegacyFields] = useState({});

  useEffect(() => {
    loadFields();
  }, [documentId]);

  const loadFields = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/documents/${documentId}/editable-fields`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // New document-wise data
        setSummary(response.data.summary || { total_docs: 0, docs_with_issues: 0, critical_errors: 0, can_generate: true });
        setDocuments(response.data.documents || []);
        setMetadata(response.data.metadata || {});
        setExtractionId(response.data.extraction_id);
        
        // Legacy fields
        setLegacyFields(response.data.fields || {});
        
        // Set active doc to first one with errors
        const firstErrorIdx = (response.data.documents || []).findIndex(d => d.doc_status === 'red');
        if (firstErrorIdx >= 0) {
          setActiveDocIndex(firstErrorIdx);
        }
      }
    } catch (err) {
      console.error('Error loading fields:', err);
      setError(err.response?.data?.detail || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  // Handle field change for a specific document
  const handleFieldChange = (fieldName, value) => {
    setDocuments(prevDocs => {
      const newDocs = [...prevDocs];
      const doc = { ...newDocs[activeDocIndex] };
      const fields = { ...doc.fields };

      fields[fieldName] = {
        ...fields[fieldName],
        value: value,
        risk: calculateLocalRisk(fieldName, value, doc.document_type, fields),
      };

      // Extent either-or: re-evaluate the twin so its red/green flips
      // when the partner gets filled.
      if (fieldName === 'extent_sqyds' && fields.extent_sqmtrs) {
        fields.extent_sqmtrs = {
          ...fields.extent_sqmtrs,
          risk: calculateLocalRisk('extent_sqmtrs', fields.extent_sqmtrs.value, doc.document_type, fields),
        };
      } else if (fieldName === 'extent_sqmtrs' && fields.extent_sqyds) {
        fields.extent_sqyds = {
          ...fields.extent_sqyds,
          risk: calculateLocalRisk('extent_sqyds', fields.extent_sqyds.value, doc.document_type, fields),
        };
      }

      doc.fields = fields;

      // Recalculate doc status — binary HCG rule:
      // RED if any mandatory (CRITICAL_FIELDS) field is missing,
      // otherwise GREEN. No yellow tier.
      const hasRed = Object.values(fields).some(f => f.risk === 'red');
      doc.doc_status = hasRed ? 'red' : 'green';

      newDocs[activeDocIndex] = doc;
      return newDocs;
    });

    // Update summary
    updateSummary();
  };

  // Local risk calculation — binary RED/GREEN.
  // Mandatory blank → red. Filled or optional → green. No yellow.
  const calculateLocalRisk = (fieldName, value, docType, docFields = null) => {
    const strValue = (value || '').trim();
    const isCritical = CRITICAL_FIELDS.includes(fieldName);

    // Extent either-or rule — twin satisfies the mandate.
    if (
      isCritical
      && !strValue
      && (fieldName === 'extent_sqyds' || fieldName === 'extent_sqmtrs')
      && docFields
      && isExtentSatisfied(docFields)
    ) {
      return 'green';
    }

    return isCritical && !strValue ? 'red' : 'green';
  };
  
  // Update summary after changes
  const updateSummary = () => {
    setSummary(prev => {
      let criticalErrors = 0;
      let docsWithIssues = 0;
      
      documents.forEach(doc => {
        if (doc.doc_status === 'red') {
          docsWithIssues++;
        }
        Object.entries(doc.fields).forEach(([key, field]) => {
          if (CRITICAL_FIELDS.includes(key) && field.risk === 'red') {
            criticalErrors++;
          }
        });
      });
      
      return {
        ...prev,
        docs_with_issues: docsWithIssues,
        critical_errors: criticalErrors,
        can_generate: criticalErrors === 0
      };
    });
  };

  // Jump to first document with errors
  const handleJumpToErrors = () => {
    const firstErrorIdx = documents.findIndex(d => d.doc_status === 'red');
    if (firstErrorIdx >= 0) {
      setActiveDocIndex(firstErrorIdx);
    }
  };

  // Navigation
  const handlePrevDoc = () => {
    if (activeDocIndex > 0) {
      setActiveDocIndex(activeDocIndex - 1);
    }
  };

  const handleNextDoc = () => {
    if (activeDocIndex < documents.length - 1) {
      setActiveDocIndex(activeDocIndex + 1);
    }
  };

  // Save changes
  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      const token = localStorage.getItem('token');
      
      // Convert documents back to flat format for backend
      const flatFields = { ...legacyFields };
      
      // Merge document changes into flat structure
      documents.forEach((doc, idx) => {
        const prefix = doc.document_type.replace(/\s+/g, '');
        Object.entries(doc.fields).forEach(([key, field]) => {
          // Store in legacy format if applicable
          const legacyKey = `${prefix}_${idx + 1}_${key}`;
          flatFields[legacyKey] = field.value;
        });
      });
      
      const response = await axios.post(
        `${API_BASE_URL}/documents/${documentId}/update-fields`,
        flatFields,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setSuccessMessage('Document updated successfully! Your changes have been saved.');
        setTimeout(() => setSuccessMessage(''), 5000);
        // Reload to get fresh validation
        loadFields();
      }
    } catch (err) {
      console.error('Error saving fields:', err);
      alert(err.response?.data?.detail || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/documents/${documentId}/export/docx`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `legal_opinion_${metadata.bank_format || 'doc'}_edited.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting document:', err);
      alert('Failed to export document');
    } finally {
      setExporting(false);
    }
  };

  // Current active document
  const activeDoc = documents[activeDocIndex] || null;

  // Memoized check for blocking errors
  const hasBlockingErrors = useMemo(() => {
    return documents.some(doc =>
      Object.entries(doc.fields || {}).some(([key, field]) =>
        CRITICAL_FIELDS.includes(key) && field.risk === 'red'
      )
    );
  }, [documents]);

  if (loading) {
    return (
      <div className="edit-fields-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="edit-fields-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  // Fallback to legacy view if no documents found
  if (documents.length === 0) {
    return (
      <div className="edit-fields-page">
        <div className="edit-header">
          <div className="header-left">
            <button onClick={() => navigate('/dashboard')} className="btn-back">← Back</button>
            <h1>Edit Legal Opinion</h1>
            <span className="format-badge">{metadata.bank_format?.toUpperCase()}</span>
          </div>
        </div>
        <div className="no-docs-message">
          <p>No documents found for review. The extraction may not have produced document-level data.</p>
          <button onClick={() => navigate('/dashboard')} className="btn-back">Return to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-fields-page">
      {/* Header */}
      <div className="edit-header">
        <div className="header-left">
          <button onClick={() => navigate('/dashboard')} className="btn-back">← Back</button>
          <h1>Review Extracted Data</h1>
          <span className="format-badge">{metadata.bank_format?.toUpperCase()}</span>
        </div>
        <div className="header-right">
          <button onClick={handleSave} disabled={saving} className="btn-save">
            {saving ? 'Saving...' : '💾 Save Changes'}
          </button>
          <button 
            onClick={handleExport} 
            disabled={exporting || hasBlockingErrors} 
            className={`btn-export ${hasBlockingErrors ? 'btn-disabled' : ''}`}
            title={hasBlockingErrors ? 'Fix critical errors first' : 'Export document'}
          >
            {exporting ? 'Exporting...' : hasBlockingErrors ? '🔴 Fix Errors First' : '📥 Export DOCX'}
          </button>
        </div>
      </div>

      {/* Summary Bar */}
      <SummaryBar summary={summary} onJumpToErrors={handleJumpToErrors} />

      {/* Success/Error Messages */}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      {hasBlockingErrors && (
        <div className="blocking-error-banner">
          <span className="error-icon">🚨</span>
          <span>Please fix {summary.critical_errors} critical error(s) before generating the opinion.</span>
          <button onClick={handleJumpToErrors}>Jump to Errors</button>
        </div>
      )}

      {/* Progress Bar */}
      <ProgressBar 
        documents={documents} 
        activeIndex={activeDocIndex} 
        onSelect={setActiveDocIndex} 
      />

      {/* Document Tabs */}
      <div className="doc-tabs-container">
        <div className="doc-tabs">
          {documents.map((doc, idx) => (
            <DocumentTab
              key={idx}
              doc={doc}
              index={idx}
              isActive={idx === activeDocIndex}
              onClick={setActiveDocIndex}
            />
          ))}
        </div>
      </div>

      {/* Active Document Content */}
      {activeDoc && (
        <div className="document-editor">
          <div className="document-header">
            <h2>
              <span className={`doc-status-icon status-${activeDoc.doc_status}`}>
                {activeDoc.doc_status === 'red' ? '🔴' : '✅'}
              </span>
              {activeDoc.document_type}
              <span className="doc-index">Document {activeDocIndex + 1} of {documents.length}</span>
            </h2>
          </div>

          <div className="field-groups-container">
            {Object.entries(FIELD_GROUPS).map(([groupKey, groupConfig]) => (
              <FieldGroup
                key={groupKey}
                groupKey={groupKey}
                groupConfig={groupConfig}
                fields={activeDoc.fields}
                onChange={handleFieldChange}
              />
            ))}
            
            {/* Render any remaining fields not in groups */}
            {(() => {
              const groupedFields = new Set(
                Object.values(FIELD_GROUPS).flatMap(g => g.fields)
              );
              const ungroupedFields = Object.entries(activeDoc.fields)
                .filter(([key]) => !groupedFields.has(key) && !key.startsWith('_'));
              
              if (ungroupedFields.length === 0) return null;
              
              return (
                <div className="field-group">
                  <h3 className="field-group-title">📋 Other Fields</h3>
                  <div className="field-group-content">
                    {ungroupedFields.map(([fieldKey, fieldData]) => (
                      <EditableField
                        key={fieldKey}
                        fieldName={fieldKey}
                        fieldData={fieldData}
                        onChange={handleFieldChange}
                        isCritical={CRITICAL_FIELDS.includes(fieldKey)}
                      />
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Document Navigation */}
          <div className="doc-navigation">
            <button 
              onClick={handlePrevDoc} 
              disabled={activeDocIndex === 0}
              className="nav-btn prev"
            >
              ◀ Previous Document
            </button>
            <span className="nav-info">
              Document {activeDocIndex + 1} of {documents.length}
            </span>
            <button 
              onClick={handleNextDoc} 
              disabled={activeDocIndex === documents.length - 1}
              className="nav-btn next"
            >
              Next Document ▶
            </button>
          </div>
        </div>
      )}

      {/* Final Action Buttons */}
      <div className="form-actions">
        <button onClick={handleSave} disabled={saving} className="btn-save-large">
          {saving ? 'Saving...' : '💾 Save All Changes'}
        </button>
        <button 
          onClick={handleExport} 
          disabled={exporting || hasBlockingErrors} 
          className={`btn-export-large ${hasBlockingErrors ? 'btn-disabled' : ''}`}
        >
          {exporting ? 'Exporting...' : hasBlockingErrors ? '🔴 Fix Critical Errors First' : '✅ Generate Opinion'}
        </button>
      </div>
    </div>
  );
};

export default EditOpinionFieldsPage;
